/**
 * Sidebar Navigation Component
 *
 * Main navigation sidebar with route links, user info, and system status.
 * Responsive design with mobile support.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SignOutButton } from '@/components/common/SignOutButton';
import {
  LayoutDashboard,
  FileText,
  Settings,
  X,
  Activity,
  User,
  FileEdit,
  Wand2,
  Plug,
  Network,
  MonitorPlay,
  Send,
  FolderKanban,
  Users,
  DollarSign,
  Gauge,
  ShieldAlert,
  Mail,
} from 'lucide-react';

const _OWNER_ROLES: ReadonlySet<string> = new Set(['owner', 'admin']);

interface SidebarProps {
  onClose?: () => void;
}

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    description: 'System overview',
    adminOnly: false
  },
  {
    name: 'Sources',
    href: '/sources',
    icon: MonitorPlay,
    description: 'Content sources',
    adminOnly: false
  },
  {
    name: 'Creators',
    href: '/creators',
    icon: Users,
    description: 'Cross-platform creators',
    adminOnly: false
  },
  {
    name: 'Content',
    href: '/content',
    icon: FileText,
    description: 'Browse content',
    adminOnly: false
  },
  {
    name: 'Patterns',
    href: '/patterns',
    icon: Network,
    description: 'Cultural patterns',
    adminOnly: false
  },
  {
    name: 'Outputs',
    href: '/outputs',
    icon: Send,
    description: 'Publishing channels',
    adminOnly: false
  },
  {
    name: 'Templates',
    href: '/templates',
    icon: FileEdit,
    description: 'Manage templates & prompts',
    adminOnly: false
  },
  {
    name: 'Domain Prompts',
    href: '/domain-prompts',
    icon: Wand2,
    description: 'AI agent prompts',
    adminOnly: true  // Only super admins can see this
  },
  {
    name: 'Channel Roll-ups',
    href: '/channels/rollups',
    icon: Network,
    description: 'Cross-platform channel roll-ups (super-admin)',
    adminOnly: true,  // Only super admins can see this
  },
  {
    name: 'System',
    href: '/system',
    icon: Settings,
    description: 'System controls',
    adminOnly: false
  },
  {
    name: 'AI Costs',
    href: '/costs',
    icon: DollarSign,
    description: 'AI usage & costs',
    adminOnly: true
  },
  {
    name: 'AI Quality',
    href: '/quality',
    icon: Gauge,
    description: 'Judge scores per step',
    adminOnly: true
  },
  {
    name: 'MCP Usage',
    href: '/mcp-usage',
    icon: Activity,
    description: 'MCP tool call metrics',
    adminOnly: true
  },
  {
    name: 'Inbound Email',
    href: '/inbound-email',
    icon: Mail,
    description: 'Email forwarding activity',
    adminOnly: true
  },
  {
    name: 'MCP Access',
    href: '/settings/mcp',
    icon: Plug,
    description: 'API for Claude',
    adminOnly: false
  },
  {
    name: 'Projects',
    href: '/projects',
    icon: FolderKanban,
    description: 'Cross-org project diagnostics',
    adminOnly: true
  },
  {
    name: 'Portal',
    href: '/portal/projects',
    icon: FolderKanban,
    description: 'Org portal view',
    adminOnly: true
  },
  {
    // Sprint 4a — visible to super_admin OR org-owner / org-admin.
    // Uses `orgOwnerOrSuperAdmin` predicate (set below) instead of
    // the boolean `adminOnly` to allow org-owners through.
    name: 'Sentinel',
    href: '/sentinel/flags',
    icon: ShieldAlert,
    description: 'Brand-safety moderator queue',
    adminOnly: false,
    orgOwnerOrSuperAdmin: true,
  }
];

export function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const { user, isSuperAdmin, organizationRole } = useAuth();

  const isOrgOwner = organizationRole !== undefined && _OWNER_ROLES.has(organizationRole);

  // Filter navigation items based on user role. Items can opt into one of
  // three visibility predicates:
  //   * adminOnly=true              → super_admin only
  //   * orgOwnerOrSuperAdmin=true   → super_admin OR org-owner/admin
  //   * neither                     → everyone in the admin shell
  const visibleNavigation = navigation.filter(item => {
    if ((item as { orgOwnerOrSuperAdmin?: boolean }).orgOwnerOrSuperAdmin) {
      return isSuperAdmin || isOrgOwner;
    }
    return !item.adminOnly || isSuperAdmin;
  });

  return (
    <div className="flex h-full flex-col bg-card border-r border-border">
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <Activity className="h-6 w-6 text-primary" />
          <span className="text-lg font-semibold">Admin</span>
        </div>

        {/* Mobile close button */}
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="lg:hidden"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4">
        {visibleNavigation.map((item) => {
          const isActive = location.pathname === item.href ||
                          (item.href === '/articles' && location.pathname.startsWith('/articles')) ||
                          (item.href === '/videos' && location.pathname.startsWith('/videos')) ||
                          (item.href === '/sources' && location.pathname.startsWith('/sources')) ||
                          (item.href === '/channels' && location.pathname.startsWith('/channels')) ||
                          (item.href === '/patterns' && location.pathname.startsWith('/patterns')) ||
                          (item.href === '/outputs' && (location.pathname.startsWith('/outputs') || location.pathname.startsWith('/newsletters'))) ||
                          (item.href === '/templates' && location.pathname.startsWith('/templates')) ||
                          (item.href === '/costs' && location.pathname.startsWith('/costs')) ||
                          (item.href === '/quality' && location.pathname.startsWith('/quality')) ||
                          (item.href === '/mcp-usage' && location.pathname.startsWith('/mcp-usage')) ||
                          (item.href === '/settings/mcp' && location.pathname.startsWith('/settings'));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <item.icon className="h-4 w-4" />
              <div className="flex-1">
                <div>{item.name}</div>
                <div className="text-xs opacity-70">{item.description}</div>
              </div>
            </NavLink>
          );
        })}
      </nav>

      <Separator />

      {/* User section */}
      <div className="p-4 space-y-4">
        {user && (
          <NavLink
            to="/profile"
            onClick={onClose}
            className="flex items-center space-x-3 p-2 rounded-lg bg-accent/50 hover:bg-accent transition-colors cursor-pointer"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <User className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user.displayName ||
                 (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                 user.firstName ||
                 user.emailAddress ||
                 'User'}
              </p>
              <p className="text-xs text-muted-foreground">
                {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'View Profile'}
              </p>
            </div>
          </NavLink>
        )}

        <SignOutButton />

        {/* Version info */}
        <div className="text-center">
          <Badge variant="secondary" className="text-xs">
            v1.0.0
          </Badge>
        </div>
      </div>
    </div>
  );
}
